import { CameraOutlined } from "@ant-design/icons"
import { App, Avatar, Form, Upload } from "antd"
import { type UploadChangeParam } from "antd/es/upload/interface"
import { UploadTaskSnapshot } from "firebase/storage"
import { useState } from "react"

import Firestore from "#classes/Firestore"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { useSaveDoc } from "#hooks/jotai/useSaveDoc"

import type { UploadProps } from "antd"

interface AvatarFormProps {
  entityId: string | undefined
  fieldName?: string
  size?: number
}

const AvatarForm = ({
  entityId,
  fieldName = "avatar",
  size = 128,
}: AvatarFormProps) => {
  const form = Form.useFormInstance()
  const { message } = App.useApp()
  const [hovered, setHovered] = useState(false)

  const avatarUrl = Form.useWatch(fieldName, form)

  const { saveDoc: saveStudent } = useSaveDoc<StudentConstructor>(
    DOC_TYPES.STUDENTS
  )

  const customRequest: UploadProps["customRequest"] = async ({
    file,
    onSuccess,
    onError,
    onProgress,
  }) => {
    const handleProgress = (snapshot: UploadTaskSnapshot) => {
      const percent = Math.round(
        (snapshot.bytesTransferred / snapshot.totalBytes) * 100
      )
      onProgress?.({ percent })
    }

    const handleSuccess = async (url: string) => {
      form.setFieldValue(fieldName, url)
      // Ant Design's onSuccess expects (response, file)
      onSuccess?.(url, file)

      // Save the avatar URL to the student document
      if (entityId) {
        try {
          await saveStudent({ id: entityId, avatar: url })
        } catch (err) {
          console.error("Failed to save avatar to student document:", err)
          // Don't show error message here since upload succeeded
          // The form will still have the URL saved
        }
      }

      message.success("Avatar uploaded successfully")
    }

    try {
      if (!entityId) {
        throw new Error("Entity ID is required to upload avatar")
      }

      const extension = (file as File).name.split(".").pop()
      const filename = `avatar.${extension}`

      await Firestore.getInstance().uploadFile(
        file as File,
        `${entityId}/${filename}`,
        { contentType: (file as File).type },
        handleProgress,
        handleSuccess
      )
    } catch (err: unknown) {
      onError?.(err as Error)
      message.error("Failed to upload avatar")
    }
  }

  const handleUploadChange = (info: UploadChangeParam) => {
    if (info.file.status === "done") {
      const url = info.file.response || (info.file as { url?: string }).url
      if (url) {
        form.setFieldValue(fieldName, url)
      }
    }
  }

  return (
    <Upload
      customRequest={customRequest}
      onChange={handleUploadChange}
      showUploadList={false}
      accept="image/*"
    >
      <div
        style={{
          position: "relative",
          width: size,
          height: size,
          cursor: "pointer",
        }}
      >
        <Avatar
          src={avatarUrl}
          size={size}
          style={{
            border: "1px solid #d9d9d9",
          }}
        >
          {!avatarUrl && "No Image"}
        </Avatar>
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            opacity: hovered ? 1 : 0,
            transition: "opacity 0.2s",
            borderRadius: "50%",
          }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <CameraOutlined
            style={{
              fontSize: size / 4,
              color: "white",
            }}
          />
        </div>
      </div>
    </Upload>
  )
}

export default AvatarForm
